//frontend/src/pages/company/Categories.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { categoryAPI } from '../../services/api';

const Categories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',  // ✅ name (backend expects this)
    parent_id: null,
    sort_order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryAPI.getTree();
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (parentId = null) => {
    setEditingCategory(null);
    setFormData({
      name: '',
      parent_id: parentId,
      sort_order: 0
    });
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,  // ✅ name
      parent_id: category.parent_id,
      sort_order: category.sort_order || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, formData);
        alert('Ангилал амжилттай шинэчлэгдлээ');
      } else {
        await categoryAPI.create(formData);
        alert('Ангилал амжилттай үүслээ');
      }
      setShowModal(false);
      fetchCategories();
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Энэ ангиллыг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await categoryAPI.delete(id);
      alert('Ангилал амжилттай устлаа');
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const renderCategoryTree = (categories, level = 0) => {
    return categories.map((category) => (
      <div key={category.id} style={{ marginLeft: `${level * 20}px` }} className="border-b border-gray-200">
        <div className="flex items-center justify-between p-4 hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{level === 0 ? '📁' : '📄'}</span>
            <div>
              <div className="font-medium text-gray-900">{category.name}</div>
              <div className="text-xs text-gray-500">
                Эрэмбэ: {category.sort_order} | Бараа: {category.product_count || 0}
                {level === 0 && <span className="ml-2 text-blue-600">(Үндсэн ангилал)</span>}
                {level === 1 && <span className="ml-2 text-purple-600">(Дэд ангилал)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Only show "Add Sub" button for level 0 (main categories) */}
            {level === 0 && (
              <button
                onClick={() => handleCreate(category.id)}
                className="text-sm px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                title="Дэд ангилал нэмэх"
              >
                + Дэд
              </button>
            )}
            <button
              onClick={() => handleEdit(category)}
              className="text-sm px-3 py-1 text-gray-600 hover:bg-gray-50 rounded"
            >
              Засах
            </button>
            <button
              onClick={() => handleDelete(category.id)}
              className="text-sm px-3 py-1 text-red-600 hover:bg-red-50 rounded"
            >
              Устгах
            </button>
          </div>
        </div>
        {/* Only render children for main categories (level 0), stop at level 1 */}
        {level === 0 && category.children && category.children.length > 0 && renderCategoryTree(category.children, level + 1)}
      </div>
    ));
  };

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* ACTIONS BAR */}
        <div className="flex items-center justify-end">
          <button 
            onClick={() => handleCreate()} 
            className="btn btn-primary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Ангилал нэмэх</span>
          </button>
        </div>

        {/* CATEGORIES TREE */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {categories.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📂</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Ангилал байхгүй байна</h3>
              <p className="text-gray-600 mb-6">Эхний ангиллаа нэмээд эхлээрэй</p>
              <button onClick={() => handleCreate()} className="btn btn-primary">+ Ангилал нэмэх</button>
            </div>
          ) : (
            renderCategoryTree(categories)
          )}
        </div>
      </div>

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-3xl">📂</span>
              <span>{editingCategory ? 'Ангилал засах' : 'Ангилал нэмэх'}</span>
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ангиллын нэр *
                </label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                  className="admin-input" 
                  required 
                  placeholder="Жнь: Электроникс" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Эрэмбэ
                </label>
                <input 
                  type="number" 
                  value={formData.sort_order} 
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} 
                  className="admin-input" 
                  placeholder="0" 
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Болих
                </button>
                <button 
                  type="submit" 
                  className="flex-1 btn btn-primary"
                >
                  ✅ Хадгалах
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default Categories;